import type React from "react";
import {Box, Button, Drawer, IconButton, Typography} from "@mui/material";
import {CloseOutlined} from "@mui/icons-material";
import type {PtV2PlanItem} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {usePtV2ActionsQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";
import {PtV2InitiativeControl} from "~/experiments/pages/payment-terms-v2/components/PtV2InitiativeControl.tsx";

type Props = {
    open: boolean;
    title: string;
    subtitle: string;
    onClose: () => void;
    /** The plan item a savings initiative would be made from; null when there is nothing to act on. */
    action: PtV2PlanItem | null;
    children?: React.ReactNode;
};

/** Supplier detail drawer shared by Payment term leakage and Term harmonization. */
export const PtV2DetailDrawer: React.FC<Props> = ({open, title, subtitle, onClose, action, children}) => {
    const actionsQ = usePtV2ActionsQuery(open);
    const existing = action ? actionsQ.data?.find(a => a.key === action.key) : undefined;

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={open} onClose={onClose} sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            <Box className="ptv2-drawer">
                <header className="ptv2-drawer-h">
                    <div>
                        <Typography component="h2" className="ptv2-drawer-title">{title}</Typography>
                        <Typography className="ptv2-muted">{subtitle}</Typography>
                    </div>
                    <IconButton aria-label="Close" size="small" onClick={onClose}><CloseOutlined/></IconButton>
                </header>
                <div className="ptv2-drawer-b">{children}</div>
                <footer className="ptv2-drawer-f">
                    <Button variant="text" onClick={onClose}>Close</Button>
                    {action && !actionsQ.isPending && <PtV2InitiativeControl item={action} existing={existing} variant="contained"/>}
                </footer>
            </Box>
        </Drawer>
    );
};
